import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Typography,
  Grid,
  Paper,
  Box,
  CircularProgress,
  Chip,
  Tooltip,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  List,
  ListItem,
  Alert,
  AlertTitle,
  ListItemText,
  Divider,
} from "@mui/material";
import {
  Close as CloseIcon,
  Assessment as AssessmentIcon,
  TimerOutlined as TimerIcon,
  MenuBookOutlined as BookIcon,
  TrendingUp as TrendingIcon,
  ErrorOutline as ErrorOutlineIcon,
} from "@mui/icons-material";
import { format, formatDistanceToNow } from "date-fns";
import { adminAPI, readingSessionAPI } from "../../services/api";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  Cell,
} from "recharts";
import { useTranslation } from "react-i18next";
import { ar, fr } from "date-fns/locale";

const formatDurationWithTranslation = (minutes, t) => {
  if (!minutes) return "0" + t("common.time.minutes");
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  return hours > 0
    ? t("common.time.hourMinute", { hours, minutes: remainingMinutes })
    : t("common.time.minutes_only", { minutes: remainingMinutes });
};

const StatCard = ({ title, value, icon }) => (
  <Paper sx={{ p: 2, height: "100%" }}>
    <Box display="flex" alignItems="center" mb={1}>
      {icon}
      <Typography variant="h6" component="div" ml={1}>
        {title}
      </Typography>
    </Box>
    <Typography variant="h4" component="div">
      {value}
    </Typography>
  </Paper>
);

const ReadingPatternChart = ({ data, title }) => {
  const { t } = useTranslation();

  return (
    <Box sx={{ width: "100%", height: 300, p: 2 }}>
      <Typography variant="h6" gutterBottom>
        {title}
      </Typography>
      <ResponsiveContainer>
        <BarChart
          data={data}
          margin={{ top: 10, right: 30, left: 0, bottom: 5 }}
        >
          <XAxis dataKey="name" tick={{ fontSize: 12 }} />
          <YAxis
            label={{
              value: t("admin.userStats.charts.minutes"),
              angle: -90,
              position: "insideLeft",
              fontSize: 12,
            }}
            tick={{ fontSize: 12 }}
          />
          <RechartsTooltip
            formatter={(value, name) => {
              if (name === "value") {
                const hours = Math.floor(value / 60);
                const minutes = value % 60;
                return [
                  `${hours > 0 ? `${hours}h ` : ""}${minutes}m`,
                  "Reading Time",
                ];
              }
              return [value, "Sessions"];
            }}
            labelStyle={{ fontSize: 12 }}
            contentStyle={{ fontSize: 12 }}
          />
          <Bar
            name="value"
            dataKey="value"
            fill="#8884d8"
            radius={[4, 4, 0, 0]}
          >
            {data.map((entry, index) => (
              <Cell
                key={`cell-${index}`}
                fill={entry.value > 0 ? "#8884d8" : "#e0e0e0"}
              />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </Box>
  );
};

// Order days of week starting from Monday
const DAYS_ORDER = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday",
];

const formatReadingPatternData = (patterns, type = "daily") => {
  if (!patterns) return [];

  if (type === "daily") {
    // Create a map with all days initialized to zero
    const daysMap = DAYS_ORDER.reduce((acc, day) => {
      acc[day] = { name: day.substr(0, 3), value: 0, sessions: 0 };
      return acc;
    }, {});

    // Fill in actual data
    patterns.daily.forEach((p) => {
      if (daysMap[p.dayOfWeek]) {
        daysMap[p.dayOfWeek].value = p.totalMinutes;
        daysMap[p.dayOfWeek].sessions = p.sessions;
      }
    });

    // Convert map to array in correct order
    return DAYS_ORDER.map((day) => daysMap[day]);
  } else {
    // Create 24-hour array with zeros
    const hours = Array.from({ length: 24 }, (_, i) => ({
      name: `${String(i).padStart(2, "0")}:00`,
      value: 0,
      sessions: 0,
    }));

    // Fill in actual data
    patterns.hourly.forEach((p) => {
      if (p.hour >= 0 && p.hour < 24) {
        hours[p.hour].value = p.totalMinutes;
        hours[p.hour].sessions = p.sessions;
      }
    });

    return hours;
  }
};

const UserStats = ({ open, onClose, userId, username }) => {
  const [loading, setLoading] = React.useState(true);
  const [stats, setStats] = React.useState(null);
  const [sessionStats, setSessionStats] = React.useState(null);
  const { t, i18n } = useTranslation();

  // Get date-fns locale based on current language
  const dateLocale =
    {
      ar,
      fr,
    }[i18n.language] || undefined;

  const [error, setError] = React.useState(false);

  const [hasSessionError, setHasSessionError] = React.useState(false);
  const [sessionErrorDetails, setSessionErrorDetails] = React.useState(null);
  
  React.useEffect(() => {
    const fetchStats = async () => {
      if (!open) return;
      try {
        setLoading(true);
        setError(false);
        setHasSessionError(false);
        setSessionErrorDetails(null);
        
        // Get user details first
        const userStats = await adminAPI.getUserDetails(userId);
        setStats(userStats.data);
        
        // Then try to get reading session stats with our improved error handling
        const sessionStatsResponse = await readingSessionAPI.getSessionStats(userId);
        
        if (sessionStatsResponse.error) {
          // Handle the specific error case
          setHasSessionError(true);
          setSessionErrorDetails({
            code: sessionStatsResponse.errorCode,
            message: sessionStatsResponse.errorMessage
          });
          // Still set the empty data structure for proper UI rendering
          setSessionStats(sessionStatsResponse.data);
        } else {
          // Set the actual data if no error
          setSessionStats(sessionStatsResponse.data);
        }
      } catch (error) {
        console.error("Error fetching user stats:", error);
        setError(true);
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, [userId, open]);

  if (!open) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">
            {t("admin.userStats.title", { username })}
          </Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        {loading ? (
          <Box display="flex" justifyContent="center" p={4}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Box sx={{ mt: 2, p: 3, textAlign: 'center' }}>
            <Alert severity="error" sx={{ mb: 2 }}>
              {t('admin.userStats.errors.loadingFailed', { defaultValue: 'There was a problem loading user statistics' })}
            </Alert>
            {stats && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="h6" gutterBottom>
                  {t('admin.userStats.partialData', { defaultValue: 'Showing available data' })}
                </Typography>
                <Grid container spacing={3} mb={4}>
                  <Grid item xs={12} sm={6} md={6}>
                    <StatCard
                      title={t("admin.userStats.cards.booksCompleted")}
                      value={stats.totalStats.booksCompleted}
                      icon={<BookIcon color="primary" />}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={6}>
                    <StatCard
                      title={t("admin.userStats.cards.pagesRead")}
                      value={stats.totalStats.totalPagesRead.toLocaleString()}
                      icon={<AssessmentIcon color="secondary" />}
                    />
                  </Grid>
                </Grid>
              </Box>
            )}
          </Box>
        ) : hasSessionError ? (
          <Box sx={{ mt: 2 }}>
            <Alert severity="warning" icon={<ErrorOutlineIcon />} sx={{ mb: 3 }}>
              <AlertTitle>{t('admin.userStats.errors.sessionStats', { defaultValue: 'Reading Session Statistics Unavailable' })}</AlertTitle>
              <Typography variant="body2">
                {t('admin.userStats.errors.sessionStatsDetail', { 
                  defaultValue: 'The server encountered an error when retrieving reading session statistics for this user.',
                  code: sessionErrorDetails?.code || 500
                })}
              </Typography>
              <Typography variant="body2" sx={{ mt: 1, fontFamily: 'monospace', fontSize: '0.8rem', color: 'text.secondary' }}>
                Error: {sessionErrorDetails?.message || 'Unknown error'}
              </Typography>
            </Alert>
            
            {/* Show basic user stats */}
            {stats && (
              <Box sx={{ mt: 3 }}>
                <Typography variant="h6" gutterBottom>
                  {t('admin.userStats.basicStats', { defaultValue: 'Basic User Statistics' })}
                </Typography>
                <Grid container spacing={3} mb={4}>
                  <Grid item xs={12} sm={6} md={6}>
                    <StatCard
                      title={t("admin.userStats.cards.booksCompleted")}
                      value={stats.totalStats.booksCompleted}
                      icon={<BookIcon color="primary" />}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={6}>
                    <StatCard
                      title={t("admin.userStats.cards.pagesRead")}
                      value={stats.totalStats.totalPagesRead.toLocaleString()}
                      icon={<AssessmentIcon color="secondary" />}
                    />
                  </Grid>
                </Grid>
              </Box>
            )}
          </Box>
        ) : stats && sessionStats ? (
          <Box sx={{ mt: 2 }}>
            {/* Stats Cards */}
            <Grid container spacing={3} mb={4}>
              <Grid item xs={12} sm={6} md={3}>
                <StatCard
                  title={t("admin.userStats.cards.booksCompleted")}
                  value={stats.totalStats.booksCompleted}
                  icon={<BookIcon color="primary" />}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <StatCard
                  title={t("admin.userStats.cards.pagesRead")}
                  value={stats.totalStats.totalPagesRead.toLocaleString()}
                  icon={<AssessmentIcon color="secondary" />}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <StatCard
                  title={t("admin.userStats.cards.totalReadingTime")}
                  value={formatDurationWithTranslation(
                    sessionStats.totalStats.totalReadingTime,
                    t
                  )}
                  icon={<TimerIcon color="success" />}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <StatCard
                  title={t("admin.userStats.cards.avgSession")}
                  value={readingSessionAPI.formatDuration(
                    sessionStats.totalStats.averageSessionLength
                  )}
                  icon={<TrendingIcon color="warning" />}
                />
              </Grid>
            </Grid>

            {/* Charts */}
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 2 }}>
                  <ReadingPatternChart
                    data={formatReadingPatternData(
                      sessionStats.patterns,
                      "daily"
                    )}
                    title={t("admin.userStats.charts.dailyPattern")}
                  />
                </Paper>
              </Grid>
              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 2 }}>
                  <ReadingPatternChart
                    data={formatReadingPatternData(
                      sessionStats.patterns,
                      "hourly"
                    )}
                    title={t("admin.userStats.charts.hourlyPattern")}
                  />
                </Paper>
              </Grid>
            </Grid>

            {/* Recent Sessions */}
            <Typography variant="h6" gutterBottom sx={{ mt: 4 }}>
              {t("admin.userStats.recentSessions.title")}
            </Typography>
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>
                      {t("admin.userStats.recentSessions.columns.book")}
                    </TableCell>
                    <TableCell>
                      {t("admin.userStats.recentSessions.columns.started")}
                    </TableCell>
                    <TableCell>
                      {t("admin.userStats.recentSessions.columns.ended")}
                    </TableCell>
                    <TableCell>
                      {t("admin.userStats.recentSessions.columns.duration")}
                    </TableCell>
                    <TableCell>
                      {t("admin.userStats.recentSessions.columns.status")}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sessionStats.recentSessions.map((session) => (
                    <TableRow key={session.id} hover>
                      <TableCell>{session.bookTitle}</TableCell>
                      <TableCell>
                        <Tooltip
                          title={format(new Date(session.startTime), "PPpp", {
                            locale: dateLocale,
                          })}
                        >
                          <span>
                            {formatDistanceToNow(new Date(session.startTime), {
                              addSuffix: true,
                              locale: dateLocale,
                            })}
                          </span>
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        {session.endTime ? (
                          <Tooltip
                            title={format(new Date(session.endTime), "PPpp", {
                              locale: dateLocale,
                            })}
                          >
                            <span>
                              {formatDistanceToNow(new Date(session.endTime), {
                                addSuffix: true,
                                locale: dateLocale,
                              })}
                            </span>
                          </Tooltip>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            -
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        {session.status === "active" ? (
                          <Typography variant="body2" color="primary">
                            {t(
                              "admin.userStats.recentSessions.status.inProgress"
                            )}
                          </Typography>
                        ) : (
                          <Typography variant="body2">
                            {readingSessionAPI.formatDuration(session.duration)}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={t(
                            `admin.userStats.recentSessions.status.${session.status}`
                          )}
                          color={
                            session.status === "completed"
                              ? "success"
                              : session.status === "active"
                              ? "primary"
                              : "default"
                          }
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Book Ratings */}
            <Typography variant="h6" gutterBottom sx={{ mt: 4 }}>
              {t("admin.userStats.bookRatings.title")}
            </Typography>
            <List>
              {stats.bookRatings.map((rating, index) => (
                <React.Fragment key={index}>
                  <ListItem>
                    <ListItemText
                      primary={rating.bookTitle}
                      secondaryTypographyProps={{
                        component: "div",
                      }}
                      secondary={
                        <>
                          <Typography variant="body2" color="text.secondary">
                            {t("admin.userStats.bookRatings.by")}{" "}
                            {rating.author}
                          </Typography>
                          <Box
                            display="flex"
                            alignItems="center"
                            gap={1}
                            mt={1}
                          >
                            <Chip
                              size="small"
                              label={`${rating.rating}/5`}
                              color="warning"
                            />
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              {rating.ratedAt || rating.createdAt
                                ? format(
                                    new Date(
                                      rating.ratedAt || rating.createdAt
                                    ),
                                    "PPP",
                                    { locale: dateLocale }
                                  )
                                : t("common.notAvailable")}
                            </Typography>
                          </Box>
                          {rating.review && (
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{ mt: 1 }}
                            >
                              "{rating.review}"
                            </Typography>
                          )}
                        </>
                      }
                    />
                  </ListItem>
                  {index < stats.bookRatings.length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </List>
          </Box>
        ) : (
          <Typography color="error">
            {t("admin.userStats.loadError")}
          </Typography>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default UserStats;

